import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, Filter, Download, Trash2, ChevronLeft, ChevronRight,
  Phone, Mail, Calendar, Clock, User, MoreHorizontal, Check,
  Loader2, Eye, RefreshCw, Archive, ArchiveRestore, Send, UserPlus, UserCog,
  Mic, FileText, MessageSquare, Bell, Copy
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  waitlist: "bg-amber-100 text-amber-700",
  contacted: "bg-violet-100 text-violet-700",
  qualified: "bg-emerald-100 text-emerald-700",
  unsuitable: "bg-red-100 text-red-700",
  contract_sent: "bg-orange-100 text-orange-700",
  hired: "bg-green-100 text-green-700",
  duplicate: "bg-pink-100 text-pink-700",
  abandoned: "bg-gray-100 text-gray-700",
  appointment_scheduled: "bg-cyan-100 text-cyan-700",
  interview_done: "bg-teal-100 text-teal-700",
  rejected: "bg-red-100 text-red-700"
};

const STATUS_LABELS = {
  new: "Neu",
  waitlist: "Nicht erreicht",
  contacted: "Kann erst später",
  qualified: "Qualifiziert",
  unsuitable: "Ungeeignet",
  contract_sent: "Weitergeleitet an T2M",
  hired: "Eingestellt",
  duplicate: "Doppelt",
  abandoned: "Bedarf noch Klärung",
  appointment_scheduled: "Termin vereinbart",
  interview_done: "Interview erledigt",
  rejected: "Abgelehnt"
};

export const AdminLeadsList = ({ authToken, initialFilters = {}, onViewLead, projectId }) => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialFilters.status || "");
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState({ type: "", status: "" });
  const pageSize = 25;
  
  // Reminder mode
  const [reminderMode, setReminderMode] = useState(false);
  const [reminderLeads, setReminderLeads] = useState([]);
  const [smsReminderPopup, setSmsReminderPopup] = useState(null);
  const [smsCopied, setSmsCopied] = useState(false);
  
  // Email Modal States
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showPlaceholderHelp, setShowPlaceholderHelp] = useState(false);

  // Manual Lead Creation States (only for FactoryHype)
  const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [isFixingData, setIsFixingData] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: "",
    email: "",
    phone: "",
    age: "",
    wohnort: "",
    start_date: "",
    job_type: "",
    travel_ready: null,  // null = nicht angegeben
    full_time: null,     // null = nicht angegeben
    zoom_call_ok: "",
    available_days: "",
    notes: "",
    status: "new"
  });

  // Bulk Assign States
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);

  // Status folder counts
  const [statusCounts, setStatusCounts] = useState({});

  // Project badge helper
  const getProjectBadge = (projectId) => {
    if (projectId === "factoryhype_v2") {
      return <Badge className="bg-pink-100 text-pink-700 text-xs">FactoryHype V2</Badge>;
    }
    if (projectId === "factoryhype") {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">FactoryHype</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 text-xs">HypeFactory</Badge>;
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchLeads();
  }, [currentPage, statusFilter, showArchived, projectId]);

  useEffect(() => {
    if (initialFilters.status) {
      setStatusFilter(initialFilters.status);
    }
  }, [initialFilters]);

  // Fetch advisors for assignment
  useEffect(() => {
    if (assignModalOpen && projectId) {
      fetchAdvisors();
    }
  }, [assignModalOpen, projectId]);

  const fetchAdvisors = async () => {
    try {
      const response = await axios.get(`${API}/admin/advisors?project_id=${projectId}`, getAuthHeaders());
      setAdvisors(response.data.advisors || []);
    } catch (error) {
      console.error("Error fetching advisors:", error);
    }
  };

  // Fetch email templates when modal opens
  useEffect(() => {
    if (emailModalOpen) {
      fetchTemplates();
    }
  }, [emailModalOpen]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/bulk-email/templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId && templateId !== "custom") {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setEmailSubject(template.subject);
        setEmailContent(template.html_content || template.content || "");
      }
    } else {
      setEmailSubject("");
      setEmailContent("");
    }
  };

  const handleSendEmailToLeads = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Keine Leads ausgewählt");
      return;
    }
    
    if (!emailSubject || !emailContent) {
      toast.error("Bitte Betreff und Inhalt angeben");
      return;
    }

    if (!window.confirm(`Möchten Sie die E-Mail an ${selectedLeads.length} Lead(s) senden?`)) {
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await axios.post(`${API}/admin/leads/send-email`, {
        lead_ids: selectedLeads,
        template_id: selectedTemplate !== "custom" ? selectedTemplate : null,
        subject: emailSubject,
        html_content: emailContent
      }, getAuthHeaders());

      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.sent_count} gesendet, ${response.data.failed_count} fehlgeschlagen`);
      } else {
        toast.success(`${response.data.sent_count} E-Mail(s) erfolgreich gesendet!`);
      }

      // Close modal and reset
      setEmailModalOpen(false);
      setSelectedTemplate("");
      setEmailSubject("");
      setEmailContent("");
      setSelectedLeads([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const openEmailModal = () => {
    if (selectedLeads.length === 0) {
      toast.error("Bitte zuerst Leads auswählen");
      return;
    }
    setEmailModalOpen(true);
  };

  // Fix FactoryHype leads data (one-time repair)
  const handleFixFactoryHypeData = async () => {
    setIsFixingData(true);
    try {
      // First show debug info
      const debugResponse = await axios.get(`${API}/admin/debug-leads`, getAuthHeaders());
      console.log("Debug Leads:", debugResponse.data);
      
      // Show debug info in alert
      const debugData = debugResponse.data;
      const debugMessage = `DEBUG INFO:
      
Verteilung nach project_id:
${JSON.stringify(debugData.project_id_distribution, null, 2)}

FactoryHype Leads: ${debugData.factoryhype_leads_count}
HypeFactory Leads: ${debugData.hypefactory_leads_count}

Erste FactoryHype Leads:
${debugData.factoryhype_leads.map(l => `- ${l.first_name}: project_id=${l.project_id}, status=${l.status}`).join('\n')}`;
      
      alert(debugMessage);
      
      if (!window.confirm("Möchten Sie jetzt die Daten reparieren?")) {
        setIsFixingData(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/fix-factoryhype-leads`, {}, getAuthHeaders());
      const data = response.data;
      
      if (data.success) {
        let message = data.message;
        if (data.deleted_advisors > 0) {
          message += `\n${data.deleted_advisors} unbenutzte Berater gelöscht.`;
        }
        toast.success(message);
        alert(`Reparatur erfolgreich!\n\n${message}\n\nKorrekte Berater-ID: ${data.correct_advisor_id}`);
      } else {
        toast.error("Fehler: " + data.error);
      }
      
      // Reload leads
      fetchLeads();
      fetchStatusCounts();
    } catch (error) {
      console.error("Fix error:", error);
      toast.error("Fehler: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsFixingData(false);
    }
  };

  // Fetch status counts
  const fetchStatusCounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/status-counts?project_id=${projectId}&show_archived=${showArchived}`, getAuthHeaders());
      setStatusCounts(response.data.counts || {});
    } catch (error) {
      console.log("Could not fetch status counts");
    }
  };

  useEffect(() => {
    fetchStatusCounts();
    fetchReminderLeads();
  }, [projectId, showArchived]);

  const fetchReminderLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/reminder-due?project_id=${projectId}`, getAuthHeaders());
      setReminderLeads(response.data.leads || []);
    } catch (error) {
      console.log("Could not fetch reminder leads");
    }
  };

  const handleSendReminder = async (leadId) => {
    try {
      const response = await axios.post(`${API}/admin/send-reminder/${leadId}`, {}, getAuthHeaders());
      if (response.data.sms_text) {
        setSmsReminderPopup({ text: response.data.sms_text, leadId });
        setSmsCopied(false);
      }
      fetchReminderLeads();
      fetchStatusCounts();
    } catch (error) {
      toast.error("Fehler beim Senden");
    }
  };

  // Manual Lead Creation Functions (only for FactoryHype)
  const resetNewLeadForm = () => {
    setNewLead({
      first_name: "",
      email: "",
      phone: "",
      age: "",
      wohnort: "",
      start_date: "",
      job_type: "",
      travel_ready: null,
      full_time: null,
      zoom_call_ok: "",
      available_days: "",
      notes: "",
      status: "new"
    });
  };

  const handleCreateLead = async () => {
    // Validate required fields
    if (!newLead.first_name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    if (!newLead.email.trim()) {
      toast.error("E-Mail ist erforderlich");
      return;
    }
    if (!newLead.phone.trim()) {
      toast.error("Telefon ist erforderlich");
      return;
    }

    setIsCreatingLead(true);
    try {
      const response = await axios.post(`${API}/admin/leads/manual`, newLead, getAuthHeaders());
      
      if (response.data.success) {
        toast.success(`Lead "${newLead.first_name}" erfolgreich erstellt!`);
        setCreateLeadModalOpen(false);
        resetNewLeadForm();
        fetchLeads(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Leads");
    } finally {
      setIsCreatingLead(false);
    }
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: "created_at",
        sort_order: "desc",
        show_archived: showArchived
      };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      if (projectId) params.project_id = projectId;

      const response = await axios.get(`${API}/admin/leads`, {
        params,
        ...getAuthHeaders()
      });
      setLeads(response.data.leads);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Fehler beim Laden der Leads");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLeads();
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeads(prev => [...prev, leadId]);
    } else {
      setSelectedLeads(prev => prev.filter(id => id !== leadId));
    }
  };

  const handleBulkStatusChange = async () => {
    try {
      await axios.post(`${API}/admin/leads/bulk-status`, {
        lead_ids: selectedLeads,
        status: bulkAction.status
      }, getAuthHeaders());
      toast.success(`${selectedLeads.length} Leads aktualisiert`);
      setSelectedLeads([]);
      setBulkActionDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleBulkArchive = async (archive = true) => {
    try {
      await axios.post(`${API}/admin/leads/bulk-archive`, {
        lead_ids: selectedLeads,
        archive
      }, getAuthHeaders());
      toast.success(`${selectedLeads.length} Leads ${archive ? 'archiviert' : 'wiederhergestellt'}`);
      setSelectedLeads([]);
      setBulkArchiveDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedAdvisor) {
      toast.error("Bitte einen Berater auswählen");
      return;
    }
    
    setIsAssigning(true);
    try {
      const response = await axios.post(`${API}/admin/leads/bulk-assign`, {
        lead_ids: selectedLeads,
        advisor_id: selectedAdvisor
      }, getAuthHeaders());
      
      toast.success(`${response.data.assigned} Lead(s) an ${response.data.advisor_name} zugewiesen`);
      setSelectedLeads([]);
      setAssignModalOpen(false);
      setSelectedAdvisor("");
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler bei der Zuweisung");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleBulkUnassign = async () => {
    if (!window.confirm(`Zuweisung für ${selectedLeads.length} Lead(s) aufheben?`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/leads/bulk-unassign`, {
        lead_ids: selectedLeads
      }, getAuthHeaders());
      
      toast.success(`Zuweisung für ${selectedLeads.length} Lead(s) aufgehoben`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Aufheben der Zuweisung");
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${API}/admin/leads/bulk-delete`, {
        lead_ids: selectedLeads
      }, getAuthHeaders());
      toast.success(`${selectedLeads.length} Leads gelöscht`);
      setSelectedLeads([]);
      setDeleteDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleBulkDeleteByCount = async (count) => {
    if (count === -1 && !window.confirm("ACHTUNG: Alle Leads werden unwiderruflich gelöscht! Fortfahren?")) {
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/leads/bulk-delete-by-count`, {
        count: count,
        filter_type: showArchived ? "archived" : "all"
      }, getAuthHeaders());
      toast.success(`${response.data.deleted} Leads gelöscht`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleBulkArchiveByCount = async (count) => {
    try {
      const response = await axios.post(`${API}/admin/leads/bulk-archive-by-count`, {
        count: count,
        filter_type: "all"
      }, getAuthHeaders());
      toast.success(`${response.data.archived} Leads archiviert`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      if (selectedLeads.length > 0) {
        params.lead_ids = selectedLeads.join(',');
      }
      
      const endpoint = format === 'excel' ? 'export/excel' : 'export/csv';
      const response = await axios.get(`${API}/admin/${endpoint}`, {
        params,
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const filename = selectedLeads.length > 0 
        ? `leads_auswahl_${selectedLeads.length}_${new Date().toISOString().slice(0,10)}.${ext}`
        : `leads_${new Date().toISOString().slice(0,10)}.${ext}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      const exportCount = selectedLeads.length > 0 ? selectedLeads.length : 'Alle';
      toast.success(`${exportCount} Lead(s) als ${ext.toUpperCase()} exportiert`);
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  // Export for Videocall - only selected leads
  const handleExportVideocall = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Bitte mindestens einen Lead auswählen");
      return;
    }
    
    try {
      const response = await axios.get(`${API}/admin/export/videocall`, {
        params: { lead_ids: selectedLeads.join(',') },
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Check if response is actually a blob/file
      if (response.data.size === 0) {
        toast.error("Export fehlgeschlagen - leere Datei");
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `daten-videocall-${new Date().toISOString().slice(0,10)}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${selectedLeads.length} Lead(s) für Videocall exportiert`);
    } catch (error) {
      console.error("Videocall Export Error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Unbekannter Fehler";
      toast.error(`Fehler beim Videocall-Export: ${errorMessage}`);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-leads-list">
      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Suchen..."
                className="pl-9 w-48 rounded-lg"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" variant="outline" size="icon" className="rounded-lg">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          
          <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setCurrentPage(1); }}>
            <SelectTrigger className="w-40 rounded-lg" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2 ml-2">
            <Switch 
              id="show-archived"
              checked={showArchived}
              onCheckedChange={(checked) => { setShowArchived(checked); setCurrentPage(1); }}
              data-testid="show-archived-toggle"
            />
            <Label htmlFor="show-archived" className="text-sm text-slate-600 cursor-pointer">
              <Archive className="w-4 h-4 inline mr-1" />
              Archivierte
            </Label>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchLeads} 
            className="rounded-lg"
            title="Seite aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {/* Bulk Actions by Count */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {selectedLeads.length > 0 && (
                <>
                  <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)}>
                    Ausgewählte ({selectedLeads.length})
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => handleBulkDeleteByCount(10)}>
                Älteste 10 löschen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkDeleteByCount(20)}>
                Älteste 20 löschen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkDeleteByCount(50)}>
                Älteste 50 löschen
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkDeleteByCount(100)}>
                Älteste 100 löschen
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleBulkDeleteByCount(-1)} className="text-red-600 font-semibold">
                ALLE löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-lg text-amber-600 border-amber-200 hover:bg-amber-50">
                <Archive className="w-4 h-4 mr-2" />
                Archivieren
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {selectedLeads.length > 0 && (
                <>
                  <DropdownMenuItem onClick={() => setBulkArchiveDialogOpen(true)}>
                    Ausgewählte ({selectedLeads.length})
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => handleBulkArchiveByCount(10)}>
                Älteste 10 archivieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkArchiveByCount(20)}>
                Älteste 20 archivieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkArchiveByCount(50)}>
                Älteste 50 archivieren
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkArchiveByCount(100)}>
                Älteste 100 archivieren
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleBulkArchiveByCount(-1)} className="text-amber-600 font-semibold">
                ALLE archivieren
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {selectedLeads.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-lg">
                  Status ändern ({selectedLeads.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {Object.entries(STATUS_LABELS).map(([key, label]) => (
                  <DropdownMenuItem 
                    key={key}
                    onClick={() => {
                      setBulkAction({ type: 'status', status: key });
                      setBulkActionDialogOpen(true);
                    }}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Email Send Button */}
          {selectedLeads.length > 0 && (
            <Button 
              variant="outline" 
              className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={openEmailModal}
            >
              <Send className="w-4 h-4 mr-2" />
              E-Mail senden ({selectedLeads.length})
            </Button>
          )}
          
          {/* Bulk Assign Button - Show when leads are selected */}
          {selectedLeads.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="rounded-lg text-violet-600 border-violet-200 hover:bg-violet-50"
                  data-testid="assign-dropdown-btn"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Berater zuweisen ({selectedLeads.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setAssignModalOpen(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Berater zuweisen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleBulkUnassign} className="text-orange-600">
                  <User className="w-4 h-4 mr-2" />
                  Zuweisung aufheben
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {/* Manual Lead Creation Button - For FactoryHype + V2 */}
          {(projectId === "factoryhype" || projectId === "factoryhype_v2") && (
            <Button 
              variant="default" 
              className="rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
              onClick={() => setCreateLeadModalOpen(true)}
              data-testid="create-lead-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Lead erstellen
            </Button>
          )}
          
          {/* Einmaliger Reparatur-Button für FactoryHype */}
          {projectId === "factoryhype" && (
            <Button 
              variant="outline" 
              className="rounded-lg border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={handleFixFactoryHypeData}
              disabled={isFixingData}
              data-testid="fix-data-btn"
            >
              {isFixingData ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Daten reparieren
            </Button>
          )}
          
          <Button variant="outline" className="rounded-lg" onClick={() => handleExport('csv')}>
            <Download className="w-4 h-4 mr-2" />
            {selectedLeads.length > 0 ? `CSV (${selectedLeads.length})` : 'CSV (Alle)'}
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={() => handleExport('excel')}>
            <Download className="w-4 h-4 mr-2" />
            {selectedLeads.length > 0 ? `Excel (${selectedLeads.length})` : 'Excel (Alle)'}
          </Button>
          {selectedLeads.length > 0 && (
            <Button 
              variant="outline" 
              className="rounded-lg border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={handleExportVideocall}
              data-testid="export-videocall-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Daten Videocall ({selectedLeads.length})
            </Button>
          )}
        </div>
      </div>

      {/* Status Folders - Anzahl IMMER anzeigen */}
      <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
        <Button
          variant={statusFilter === "" ? "default" : "ghost"}
          size="sm"
          onClick={() => { setStatusFilter(""); setCurrentPage(1); }}
          className={`rounded-lg ${statusFilter === "" ? "bg-slate-800" : ""}`}
        >
          Alle
          <Badge className={`ml-2 ${statusFilter === "" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
            {statusCounts._total || total || 0}
          </Badge>
        </Button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <Button
            key={key}
            variant={statusFilter === key && !reminderMode ? "default" : "ghost"}
            size="sm"
            onClick={() => { setStatusFilter(key); setReminderMode(false); setCurrentPage(1); }}
            className={`rounded-lg ${statusFilter === key && !reminderMode ? "bg-slate-800" : ""}`}
          >
            {label}
            <Badge className={`ml-2 ${statusFilter === key && !reminderMode ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
              {statusCounts[key] || 0}
            </Badge>
          </Button>
        ))}
        {/* Erinnerung fällig Tab - immer sichtbar, blinkt rot wenn fällig */}
          <Button
            variant={reminderMode ? "default" : "ghost"}
            size="sm"
            onClick={() => { setReminderMode(!reminderMode); setStatusFilter(""); setCurrentPage(1); }}
            className={`rounded-lg ${reminderMode ? "bg-red-600 hover:bg-red-700" : statusCounts.reminder_due > 0 ? "text-red-700 border-2 border-red-400 bg-red-50 animate-pulse" : "text-slate-500 border border-slate-200"}`}
            data-testid="reminder-due-tab"
          >
            <Bell className="w-3.5 h-3.5 mr-1" />
            Erinnerung fällig
            <Badge className={`ml-2 ${reminderMode ? "bg-white/20 text-white" : statusCounts.reminder_due > 0 ? "bg-red-500 text-white" : "bg-slate-200 text-slate-500"}`}>
              {statusCounts.reminder_due || 0}
            </Badge>
          </Button>
      </div>

      {/* Stats Bar */}
      <div className="text-sm text-slate-500">
        {reminderMode ? `${reminderLeads.length} Erinnerung(en) fällig` : (
          <>{total} Lead{total !== 1 ? 's' : ''} gefunden
          {selectedLeads.length > 0 && ` • ${selectedLeads.length} ausgewählt`}</>
        )}
      </div>

      {/* Reminder Mode Table */}
      {reminderMode ? (
        <Card className="bg-white border-amber-200 rounded-xl">
          <CardContent className="p-0">
            {reminderLeads.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Bell className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p>Keine Erinnerungen fällig</p>
                <p className="text-xs mt-1">Leads erscheinen hier 48h nach Qualifizierung</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-50 border-b border-amber-200">
                    <th className="p-3 text-left text-xs font-medium text-amber-700 uppercase">Name</th>
                    <th className="p-3 text-left text-xs font-medium text-amber-700 uppercase">Kontakt</th>
                    <th className="p-3 text-left text-xs font-medium text-amber-700 uppercase">E-Mail Status</th>
                    <th className="p-3 text-left text-xs font-medium text-amber-700 uppercase">Fällig seit</th>
                    <th className="p-3 text-right text-xs font-medium text-amber-700 uppercase">SMS Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-slate-100 hover:bg-amber-50/50">
                      <td className="p-3">
                        <span className="font-medium text-slate-900">{lead.first_name}</span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <span
                            onClick={() => { if (window.callRelayTrigger) window.callRelayTrigger(lead.phone, lead.first_name, lead.id); }}
                            className="text-sm text-emerald-600 hover:text-emerald-700 cursor-pointer hover:underline flex items-center gap-1"
                          >
                            <Phone className="w-3 h-3" />{lead.phone}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">{lead.email}</div>
                      </td>
                      <td className="p-3">
                        {lead.reminder_email_sent ? (
                          <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1 w-fit">
                            <Check className="w-3 h-3" /> E-Mail gesendet
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-100 text-blue-700 text-xs w-fit">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Wird gesendet...
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-sm text-amber-600 font-medium">
                        {lead.reminder_scheduled_at ? new Date(lead.reminder_scheduled_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSendReminder(lead.id)}
                            className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                            data-testid={`send-reminder-${lead.id}`}
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            SMS senden
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onViewLead(lead.id)}
                            className="rounded-lg"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : (
      <>
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : leads.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-16 text-center">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Keine Leads gefunden</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-left">
                    <Checkbox 
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Erstellt</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Projekt</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Name</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Alter</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Wohnort</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Aktuell</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Startklar</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Kontakt</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Berater</th>
                  <th className="p-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead, index) => (
                  <motion.tr 
                    key={lead.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="hover:bg-slate-50 transition-colors"
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td className="p-3">
                      <Checkbox 
                        checked={selectedLeads.includes(lead.id)}
                        onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                      />
                    </td>
                    <td className="p-3 text-sm text-slate-600">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="p-3">
                      {getProjectBadge(lead.project_id)}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{lead.first_name}</span>
                        {/* Voice & Resume indicators */}
                        <div className="flex items-center gap-1">
                          {(lead.voice_message?.has_voice || lead.has_voice_message) && (
                            <Mic className="w-3.5 h-3.5 text-purple-500" title="Sprachnachricht vorhanden" />
                          )}
                          {lead.resume_files && lead.resume_files.length > 0 && (
                            <FileText className="w-3.5 h-3.5 text-amber-500" title="Bewerbungsunterlagen vorhanden" />
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-slate-600">{lead.age}</td>
                    <td className="p-3 text-sm text-slate-600">{lead.wohnort || '-'}</td>
                    <td className="p-3 text-sm text-slate-600 max-w-[120px] truncate" title={lead.current_work_status || '-'}>
                      {lead.current_work_status || '-'}
                    </td>
                    <td className="p-3 text-sm text-slate-600">{lead.start_date}</td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.callRelayTrigger) {
                                window.callRelayTrigger(lead.phone, lead.first_name, lead.id);
                              } else {
                                window.location.href = `tel:${lead.phone}`;
                              }
                            }}
                            className="text-sm text-emerald-600 hover:text-emerald-700 cursor-pointer hover:underline flex items-center gap-1"
                            title="Anrufen via CallRelay"
                          >
                            <Phone className="w-3 h-3" />
                            {lead.phone}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const msg = prompt('SMS-Text eingeben:');
                              if (msg && window.callRelaySms) {
                                window.callRelaySms(lead.phone, msg, lead.first_name, lead.id);
                              }
                            }}
                            className="p-0.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                            title="SMS senden"
                          >
                            <MessageSquare className="w-3 h-3" />
                          </button>
                        </div>
                        <a href={`mailto:${lead.email}`} className="text-sm text-slate-600 hover:underline flex items-center gap-1 truncate max-w-[180px]">
                          <Mail className="w-3 h-3" />
                          {lead.email}
                        </a>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Badge className={`${STATUS_COLORS[lead.status]} border-0`}>
                          {STATUS_LABELS[lead.status]}
                        </Badge>
                        {lead.archived && (
                          <Badge variant="secondary" className="text-xs">
                            <Archive className="w-3 h-3 mr-1" />
                            Archiviert
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {lead.assigned_to_name ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0">
                          <User className="w-3 h-3 mr-1" />
                          {lead.assigned_to_name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="rounded-lg"
                        onClick={() => onViewLead(lead.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isLoading}
            className="rounded-lg"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Zurück
          </Button>
          
          <span className="text-sm text-slate-600 px-4">
            Seite {currentPage} von {totalPages}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || isLoading}
            className="rounded-lg"
          >
            Weiter
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      </>
      )}

      {/* SMS Reminder Popup */}
      {smsReminderPopup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSmsReminderPopup(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg text-amber-700 flex items-center gap-2 mb-3">
              <MessageSquare className="w-5 h-5" />
              Erinnerungs-SMS versenden
            </h3>
            <p className="text-sm text-slate-500 mb-3">E-Mail wurde automatisch versendet. Bitte jetzt SMS über CallRelay senden:</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap mb-4">
              {smsReminderPopup.text}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="rounded-lg flex-1"
                onClick={() => {
                  navigator.clipboard.writeText(smsReminderPopup.text);
                  setSmsCopied(true);
                  toast.success("SMS-Text kopiert!");
                  setTimeout(() => setSmsCopied(false), 2000);
                }}
              >
                {smsCopied ? <Check className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
                {smsCopied ? "Kopiert!" : "Text kopieren"}
              </Button>
              <Button
                className="rounded-lg flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  const lead = reminderLeads.find(l => l.id === smsReminderPopup.leadId);
                  if (window.callRelaySms && lead) {
                    window.callRelaySms(lead.phone, smsReminderPopup.text, lead.first_name, lead.id);
                    toast.success("SMS wird über CallRelay gesendet!");
                  } else {
                    navigator.clipboard.writeText(smsReminderPopup.text);
                    toast.info("Text kopiert");
                  }
                  setSmsReminderPopup(null);
                }}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Via CallRelay
              </Button>
              <Button variant="ghost" className="rounded-lg" onClick={() => setSmsReminderPopup(null)}>
                Schließen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Dialog */}
      <AlertDialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Status ändern</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedLeads.length} Lead(s) auf &quot;{STATUS_LABELS[bulkAction.status]}&quot; setzen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkStatusChange} className="rounded-lg">
              Ändern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Archive Dialog */}
      <AlertDialog open={bulkArchiveDialogOpen} onOpenChange={setBulkArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leads archivieren</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedLeads.length} Lead(s) werden archiviert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleBulkArchive(true)} className="bg-amber-600 hover:bg-amber-700 rounded-lg">
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leads löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedLeads.length} Lead(s) werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 rounded-lg">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              E-Mail an {selectedLeads.length} Lead(s) senden
            </DialogTitle>
            <DialogDescription>
              Wähle eine Vorlage oder schreibe eine eigene Nachricht.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-4">
            {/* Left side - Editor */}
            <div className="space-y-4">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Vorlage auswählen</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Vorlage wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">📝 Eigene Nachricht</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <Label>Betreff</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="E-Mail Betreff..."
                  className="rounded-lg"
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Inhalt</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPlaceholderHelp(!showPlaceholderHelp)}
                    className="text-xs text-blue-600"
                  >
                    {showPlaceholderHelp ? "Platzhalter ausblenden" : "Platzhalter anzeigen"}
                  </Button>
                </div>
                
                {showPlaceholderHelp && (
                  <div className="bg-blue-50 p-3 rounded-lg text-sm">
                    <p className="font-semibold mb-2">Verfügbare Platzhalter:</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <span><code className="bg-white px-1 rounded">%vorname%</code> - Vorname</span>
                      <span><code className="bg-white px-1 rounded">%nachname%</code> - Nachname</span>
                      <span><code className="bg-white px-1 rounded">%email%</code> - E-Mail</span>
                      <span><code className="bg-white px-1 rounded">%telefon%</code> - Telefon</span>
                      <span><code className="bg-white px-1 rounded">%alter%</code> - Alter</span>
                      <span><code className="bg-white px-1 rounded">%status%</code> - Status</span>
                      <span><code className="bg-white px-1 rounded">%termin_datum%</code> - Termin Datum</span>
                      <span><code className="bg-white px-1 rounded">%start_datum%</code> - Startdatum</span>
                    </div>
                  </div>
                )}
                
                <Textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  placeholder="Hallo %vorname%,&#10;&#10;..."
                  className="rounded-lg min-h-[200px] font-mono text-sm"
                />
              </div>
            </div>

            {/* Right side - Preview */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Vorschau (wie beim Empfänger)
              </Label>
              <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                {/* Email Header Preview */}
                <div className="bg-slate-50 border-b border-slate-200 p-3 text-sm">
                  <div className="flex gap-2">
                    <span className="text-slate-500 font-medium">Betreff:</span>
                    <span className="text-slate-700">
                      {emailSubject
                        .replace(/%vorname%/gi, "Max")
                        .replace(/%nachname%/gi, "Mustermann")
                        .replace(/%email%/gi, "max@beispiel.de")
                        .replace(/%telefon%/gi, "+49 123 456789")
                        || "(Kein Betreff)"}
                    </span>
                  </div>
                </div>
                {/* Email Body Preview - renders HTML properly */}
                <iframe
                  title="E-Mail Vorschau"
                  className="w-full min-h-[300px] border-0"
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <meta charset="utf-8">
                      <style>
                        body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; padding: 16px; margin: 0; color: #333; }
                        img { max-width: 100%; height: auto; }
                        a { color: #2563eb; }
                        table { border-collapse: collapse; }
                      </style>
                    </head>
                    <body>
                      ${emailContent
                        ? emailContent
                            .replace(/%vorname%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Max</strong>")
                            .replace(/%nachname%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Mustermann</strong>")
                            .replace(/%email%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>max@beispiel.de</strong>")
                            .replace(/%telefon%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>+49 123 456789</strong>")
                            .replace(/%alter%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>25</strong>")
                            .replace(/%status%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Neu</strong>")
                            .replace(/\{name\}/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Max</strong>")
                        : "<p style='color:#94a3b8;font-style:italic;'>Vorschau erscheint hier...</p>"
                      }
                    </body>
                    </html>
                  `}
                />
              </div>
              <p className="text-xs text-slate-500">
                Platzhalter werden mit Beispieldaten angezeigt (gelb markiert). Im echten Versand werden die tatsächlichen Lead-Daten eingesetzt.
              </p>
              
              {/* Recipient Info */}
              <div className="bg-slate-50 p-3 rounded-lg text-sm mt-2">
                <p className="text-slate-600">
                  <strong>Empfänger:</strong> {selectedLeads.length} Lead(s) ausgewählt
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setEmailModalOpen(false)}
              className="rounded-lg"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSendEmailToLeads}
              disabled={isSendingEmail || !emailSubject || !emailContent}
              className="rounded-lg bg-blue-600 hover:bg-blue-700"
            >
              {isSendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sende...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Lead Creation Modal - Only for FactoryHype */}
      <Dialog open={createLeadModalOpen} onOpenChange={setCreateLeadModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Lead manuell erstellen
            </DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Lead mit manuell eingegebenen Daten.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Required Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newLead.first_name}
                  onChange={(e) => setNewLead({ ...newLead, first_name: e.target.value })}
                  placeholder="Vor- und Nachname"
                  className="rounded-lg"
                  data-testid="new-lead-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  E-Mail <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="email@beispiel.de"
                  className="rounded-lg"
                  data-testid="new-lead-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Telefon <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  className="rounded-lg"
                  data-testid="new-lead-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Alter</Label>
                <Input
                  value={newLead.age}
                  onChange={(e) => setNewLead({ ...newLead, age: e.target.value })}
                  placeholder="z.B. 25"
                  className="rounded-lg"
                  data-testid="new-lead-age"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Wohnort</Label>
                <Input
                  value={newLead.wohnort}
                  onChange={(e) => setNewLead({ ...newLead, wohnort: e.target.value })}
                  placeholder="z.B. Berlin"
                  className="rounded-lg"
                  data-testid="new-lead-wohnort"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Startklar ab</Label>
                <Input
                  value={newLead.start_date}
                  onChange={(e) => setNewLead({ ...newLead, start_date: e.target.value })}
                  placeholder="z.B. Sofort, Ab 01.04."
                  className="rounded-lg"
                  data-testid="new-lead-start-date"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Job-Typ</Label>
                <Select 
                  value={newLead.job_type} 
                  onValueChange={(value) => setNewLead({ ...newLead, job_type: value })}
                >
                  <SelectTrigger className="rounded-lg" data-testid="new-lead-job-type">
                    <SelectValue placeholder="Job-Typ wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="promotour">Promotour</SelectItem>
                    <SelectItem value="messe">Messe</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="verkauf">Verkauf</SelectItem>
                    <SelectItem value="nebenjob">Nebenjob</SelectItem>
                    <SelectItem value="studentenjob">Studentenjob</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Verfügbare Tage</Label>
                <Input
                  value={newLead.available_days}
                  onChange={(e) => setNewLead({ ...newLead, available_days: e.target.value })}
                  placeholder="z.B. Mo-Fr, Wochenende"
                  className="rounded-lg"
                  data-testid="new-lead-available-days"
                />
              </div>
            </div>

            {/* Qualification Dropdowns - Optional fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Reisebereit</Label>
                <Select 
                  value={newLead.travel_ready === true ? "ja" : newLead.travel_ready === false ? "nein" : ""} 
                  onValueChange={(value) => setNewLead({ ...newLead, travel_ready: value === "ja" ? true : value === "nein" ? false : null })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">Ja</SelectItem>
                    <SelectItem value="nein">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Vollzeit</Label>
                <Select 
                  value={newLead.full_time === true ? "ja" : newLead.full_time === false ? "nein" : ""} 
                  onValueChange={(value) => setNewLead({ ...newLead, full_time: value === "ja" ? true : value === "nein" ? false : null })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">Ja</SelectItem>
                    <SelectItem value="nein">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Zoom-Call</Label>
                <Select 
                  value={newLead.zoom_call_ok} 
                  onValueChange={(value) => setNewLead({ ...newLead, zoom_call_ok: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Nicht angegeben" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ja, Video-Call passt">Ja, Video-Call passt</SelectItem>
                    <SelectItem value="Nein, lieber telefonieren">Nein, lieber telefonieren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Status</Label>
                <Select 
                  value={newLead.status} 
                  onValueChange={(value) => setNewLead({ ...newLead, status: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Interne Notizen</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                placeholder="Zusätzliche Informationen zum Lead..."
                className="rounded-lg min-h-[80px]"
                data-testid="new-lead-notes"
              />
            </div>

            {/* Info Box */}
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm">
              <p className="text-emerald-700">
                <strong>Hinweis:</strong> Dieser Lead wird als "Manuell" erstellt markiert und erscheint in der normalen Lead-Liste.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setCreateLeadModalOpen(false);
                resetNewLeadForm();
              }}
              className="rounded-lg"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={isCreatingLead || !newLead.first_name || !newLead.email || !newLead.phone}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-create-lead"
            >
              {isCreatingLead ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Erstelle...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Lead erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Modal */}
      <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-violet-600" />
              Leads zuweisen
            </DialogTitle>
            <DialogDescription>
              {selectedLeads.length} Lead(s) einem Berater zuweisen
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Berater auswählen</Label>
              {advisors.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <p>Keine Berater für dieses Projekt vorhanden.</p>
                  <p className="mt-1">Bitte erstellen Sie zuerst einen Berater unter "Berater".</p>
                </div>
              ) : (
                <Select value={selectedAdvisor} onValueChange={setSelectedAdvisor}>
                  <SelectTrigger className="rounded-lg" data-testid="select-advisor">
                    <SelectValue placeholder="Berater wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {advisors.map((advisor) => (
                      <SelectItem key={advisor.id} value={advisor.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {advisor.name} ({advisor.email})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Selected leads preview */}
            <div className="bg-slate-50 p-3 rounded-lg text-sm">
              <p className="text-slate-600">
                <strong>{selectedLeads.length}</strong> Lead(s) werden dem ausgewählten Berater zugewiesen.
              </p>
              <p className="text-slate-500 text-xs mt-1">
                Der Berater kann diese Leads dann in seinem Dashboard bearbeiten.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setAssignModalOpen(false);
                setSelectedAdvisor("");
              }}
              className="rounded-lg"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={isAssigning || !selectedAdvisor || advisors.length === 0}
              className="rounded-lg bg-violet-600 hover:bg-violet-700"
              data-testid="confirm-assign"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Zuweisen...
                </>
              ) : (
                <>
                  <UserCog className="w-4 h-4 mr-2" />
                  Zuweisen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeadsList;
